import React from 'react';
import { FilterState } from '../../types';
import { Slider } from '../ui/range-slider';
import { PropertyTypeToggle } from './PropertyTypeToggle';
import { LocationSelect } from './LocationSelect';
import { X } from 'lucide-react';

interface FilterPanelProps {
    filters: FilterState;
    onFilterChange: (filters: Partial<FilterState>) => void;
    onClose: () => void;
    onClear: () => void;
    resultsCount?: number;
    hideFooter?: boolean;
}

export const FilterPanel: React.FC<FilterPanelProps> = ({
    filters, onFilterChange, onClose, onClear, resultsCount, hideFooter
}) => {

    // Helpers to update filter state
    const updateLocations = (val: string[]) => onFilterChange({ locations: val });
    const updatePrice = (val: number[]) => onFilterChange({ priceRange: [val[0], val[1]] });
    const updateSqft = (val: number[]) => onFilterChange({ sqftRange: [val[0], val[1]] });
    const updateType = (val: string[]) => onFilterChange({ type: val });
    const updateBeds = (val: number | null) => onFilterChange({ beds: val });
    const updateCompletion = (val: string[]) => onFilterChange({ completion: val });

    return (
        <div className="flex flex-col h-full bg-white shadow-xl w-full max-w-md ml-auto">
            {/* Header - Only show when not in sheet mode */}
            {!hideFooter && (
                <div className="flex items-center justify-between p-4 md:p-6 border-b border-slate-100">
                    <h2 className="text-lg md:text-xl font-bold text-slate-900">Filters</h2>
                    <button 
                        onClick={onClose} 
                        aria-label="Close filters"
                        className="p-2 hover:bg-slate-100 rounded-full text-slate-500"
                    >
                        <X size={20} />
                    </button>
                </div>
            )}

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4 md:space-y-8">

                {/* Location Select */}
                <div className="space-y-3">
                    <span className="text-sm font-semibold text-slate-700">Location</span>
                    <LocationSelect selectedLocations={filters.locations} onChange={updateLocations} />
                </div>

                {/* Price Slider */}
                <div className="space-y-4">
                    <div className="flex justify-between items-center bg-slate-50 p-2 rounded-lg">
                        <span className="text-sm font-semibold text-slate-700">Price Range</span>
                        <span className="text-sm font-mono text-slate-500">
                            {formatCurrency(filters.priceRange[0])} - {formatCurrency(filters.priceRange[1])}+
                        </span>
                    </div>
                    <Slider
                        defaultValue={[0, 50000000]}
                        value={[filters.priceRange[0], filters.priceRange[1]]}
                        max={50000000}
                        step={100000}
                        onValueChange={updatePrice}
                    />
                    <div className="flex justify-between text-xs text-slate-400">
                        <span>0 AED</span>
                        <span>50M+ AED</span>
                    </div>
                </div>

                {/* Property Type */}
                <div className="space-y-3">
                    <span className="text-sm font-semibold text-slate-700">Property Type</span>
                    <PropertyTypeToggle selectedTypes={filters.type} onChange={updateType} />
                </div>

                {/* Bedrooms */}
                <div className="space-y-3">
                    <span className="text-sm font-semibold text-slate-700">Bedrooms</span>
                    <div className="flex gap-2">
                        {[null, 1, 2, 3, 4].map(b => (
                            <button
                                key={String(b)}
                                onClick={() => updateBeds(b)}
                                className={`
                                    flex-1 h-10 rounded-lg border text-sm font-medium transition-all
                                    ${filters.beds === b
                                        ? 'bg-slate-900 border-slate-900 text-white'
                                        : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}
                                `}
                            >
                                {b === null ? 'Any' : b === 4 ? '4+' : b}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Size Slider */}
                <div className="space-y-4">
                    <div className="flex justify-between items-center bg-slate-50 p-2 rounded-lg">
                        <span className="text-sm font-semibold text-slate-700">Size (Sq.ft)</span>
                        <span className="text-sm font-mono text-slate-500">
                            {filters.sqftRange[0]} - {filters.sqftRange[1] === 10000 ? '10k+' : filters.sqftRange[1]}
                        </span>
                    </div>
                    <Slider
                        defaultValue={[0, 10000]}
                        value={[filters.sqftRange[0], filters.sqftRange[1]]}
                        max={10000}
                        step={100}
                        onValueChange={updateSqft}
                    />
                </div>

                {/* Completion */}
                <div className="space-y-3">
                    <span className="text-sm font-semibold text-slate-700">Completion</span>
                    <div className="grid grid-cols-3 gap-2">
                        {(() => {
                            const currentYear = new Date().getFullYear();
                            const years = ['Any', 'Ready'];
                            for (let i = 0; i <= 5; i++) {
                                years.push(String(currentYear + i));
                            }
                            return years.map(year => {
                                const isSelected = filters.completion.includes(year) || (year === 'Any' && filters.completion.length === 0);

                                return (
                                    <button
                                        key={year}
                                        onClick={() => {
                                            let newCompletion = [...filters.completion];
                                            if (year === 'Any') {
                                                newCompletion = [];
                                            } else {
                                                if (newCompletion.includes(year)) {
                                                    newCompletion = newCompletion.filter(y => y !== year);
                                                } else {
                                                    newCompletion.push(year);
                                                }
                                            }
                                            updateCompletion(newCompletion);
                                        }}
                                        className={`
                                            h-10 rounded-lg border text-sm font-medium transition-all
                                            ${isSelected
                                                ? 'bg-slate-900 border-slate-900 text-white'
                                                : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'}
                                        `}
                                    >
                                        {year}
                                    </button>
                                );
                            });
                        })()}
                    </div>
                </div>

            </div>

            {/* Footer */}
            {!hideFooter && (
                <div className="p-4 md:p-6 border-t border-slate-100 bg-slate-50 mt-auto">
                    <div className="flex gap-3">
                        <button
                            onClick={onClear}
                            className="flex-1 h-12 rounded-xl border border-slate-200 bg-white text-slate-700 font-semibold hover:bg-slate-50"
                        >
                            Clear All
                        </button>
                        <button
                            onClick={onClose}
                            className="flex-[2] h-12 rounded-xl bg-slate-900 text-white font-semibold hover:bg-slate-800 shadow-lg shadow-slate-900/20"
                        >
                            {typeof resultsCount !== 'undefined' ? `Show ${resultsCount} Homes` : 'Show Results'}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

const formatCurrency = (val: number) => {
    if (val >= 1000000) return `${(val / 1000000).toFixed(1)}M`;
    if (val >= 1000) return `${(val / 1000).toFixed(0)}k`;
    return val;
}
